import { AccordionDetails, AccordionSummary, Grid, styled } from "@mui/material";
import FormField from "components/FormField/FormField";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { UpdateProposalProductInputSchema, getDefaultValues } from "DDD/action-objects/StageProductUpdate";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  ProductMeasurementJob,
  ProductMeasurementMaterial,
  ProposalProduct,
  ProposalStage,
  UpdateProposalProductInput,
  EquipmentPieceChargeType,
  ProductLabourCostType,
} from "generated/graphql";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import MDButton from "components/MDButton";
import Accordion from "@mui/material/Accordion";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import useUpdateProposalProduct from "hooks/proposals/useUpdateProposalProduct";
import { evolve } from "ramda";
import StageProductContext from "./forms/StageProductContext";
import EquipmentCostsSubForm from "./forms/subforms/EquipmentCostsSubForm";
import LabourCostsSubForm from "./forms/subforms/LabourCostsSubForm";
import AdditionalCostsSubForm from "./forms/subforms/AdditionalCostsSubForm";
import { ProposalContext } from "./providers/ProposalContextProvider";
import MaterialCosts from "./MaterialCosts";
import AddMaterialModal from "./AddMaterialModal";
import { ProposalFormsContext } from "./providers/ProposalFormsContextProvider";
import measurementJobTranslations from "constants/measurementJobTranslations";
import ProposalDefaultsAndInputs from "./ProposalDefaultsAndInputs";
import useModalState from "hooks/useModalState";
import MapModal from "modules/Mapmodal/MapModal";
import measurementMapTranslations from "constants/measurementMapTranslation";
import ProposalProductFiles from "./ProposalProductFiles";
import { MapOutput } from "interfaces/interfaces";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import getLocalString from "constants/Localization";
import useGlobalMessage from "hooks/notifications/useGlobalMessage";
import DirtyFieldsTracker from "./DirtyFieldsTracker";
import CurrencyFormField from "components/CurrencyFormField/CurrencyFormField";
import currencyEndAdornment from "constants/currencyEndAdornment";
import DeleteStageProduct from "./buttons/Stage/DeleteStageProduct";
import AddProjectFileToProposal from "./AddProjectFileToProposal";
import AddFilesToProduct from "./AddFilesToProduct";
import AddMaterialNoPlantModal from "./AddMaterialNoPlantModal";
import { cleanMaterialNoPlants, getMaterialNoPlantInputError } from "./utils/materialNoPlants";
import getJobSizeUnitLabel from "utils/proposals/getJobSizeUnitLabel";
import getGraphQLErrorMessage from "utils/graphQL/getGraphQLErrorMessage";

export interface StageProductProps {
  product: ProposalProduct;
  stage: ProposalStage;
  transferTrucks: ProposalProduct["transferTrucks"];
  expandedProduct: ProposalProduct["id"] | false;
  mapData?: string;
  handleDelete?: (id: string) => void;
}

const sanitizeMaterialMarkup = (markup: unknown): number | null => {
  if (typeof markup === "number" && !isNaN(markup) && markup >= 0 && markup <= 100) {
    return markup;
  }

  return null;
};

const dollarsToCentsOrNull = (value: number | string | null | undefined): number | null => {
  if (value === "" || value == null) {
    return null;
  }

  const parsedValue = typeof value === "string" ? Number(value) : value;

  if (typeof parsedValue !== "number" || isNaN(parsedValue)) {
    return null;
  }

  return Math.round(parsedValue * 100);
};

const numberOrNull = (value: number | string | null | undefined): number | null => {
  if (value === "" || value == null) {
    return null;
  }

  const parsedValue = typeof value === "string" ? Number(value) : value;

  if (typeof parsedValue !== "number" || isNaN(parsedValue)) {
    return null;
  }

  return parsedValue;
};

const StageProduct = React.memo(function StageProductContainer({
  product,
  stage,
  transferTrucks,
  expandedProduct,
  mapData,
  handleDelete,
}: StageProductProps) {
  const { show } = useGlobalMessage();
  const { inViewMode } = useContext(ProposalContext);
  const mapImageInterval = useRef<NodeJS.Timer | null>(null);
  const isMountedRef = useRef(true);
  const saveInFlightRef = useRef(false);
  const pendingSaveRef = useRef(false);
  const {
    setProductSubmitFunction,
    setProductDirtyStatus,
    setProductFormStatus,
    organizationSettings,
  } = useContext(ProposalFormsContext);
  const [updateProposalProduct, { loading, isSuccess, getData }] = useUpdateProposalProduct();
  const { open, onOpen, onClose } = useModalState();
  const {
    open: addMaterialModalOpen,
    onOpen: onAddMaterialModalOpen,
    onClose: onAddMaterialModalClose,
  } = useModalState();
  const {
    open: addMaterialNoPlantModalOpen,
    onOpen: onAddMaterialNoPlantModalOpen,
    onClose: onAddMaterialNoPlantModalClose,
  } = useModalState();
  const [mapDataProduct, setMapDataProduct] = useState(product.mapData ? product.mapData : mapData);
  const isSubcontracted = product.material.subcontracted;
  const measurementJobUnit = product.material.measurementJob === ProductMeasurementJob.UNIT;
  const isUnitPriced = product.material.unitPriceProduct;
  const averageWorkingHoursPerDay = +(organizationSettings?.["average_working_hours_per_day"] ?? 0);

  const defaultValues: UpdateProposalProductInputSchema = useMemo(() => {
    return getDefaultValues({
      product,
      transferTrucks,
      averageWorkingHoursPerDay,
    });
  }, [product, transferTrucks, averageWorkingHoursPerDay]);

  const methods = useForm<UpdateProposalProductInputSchema>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues,
  });

  const {
    register,
    control,
    getValues,
    setValue,
    formState: { isDirty, isSubmitSuccessful },
  } = methods;

  const convertInputHoursToSeconds = useCallback((field) => {
    if (field === 0) {
      return 0;
    }
    return field ? Math.floor(field * 3600) : null;
  }, []);

  const convertInputDaysToSeconds = useCallback((field) => {
    if (field === 0) {
      return 0;
    }
    return field ? Math.floor(field * 3600) : null;
  }, []);

  const onSubmit = useCallback(
    async (input: UpdateProposalProductInputSchema) => {
      if (saveInFlightRef.current) {
        pendingSaveRef.current = true;
        return { success: false, data: null };
      }

      saveInFlightRef.current = true;
      pendingSaveRef.current = false;
      (document.activeElement as HTMLElement).blur();
      setProductFormStatus(product.id, "pending");
      // Hide pending-changes banner while a save is in progress
      setProductDirtyStatus(product.id, false);

      try {
        const materialNoPlantError = getMaterialNoPlantInputError(input.materialNoPlants);
        if (materialNoPlantError) {
          setProductFormStatus(product.id, "rejected");
          setProductDirtyStatus(product.id, true);
          show({
            message: materialNoPlantError,
            options: { variant: "error" },
          });
          return { success: false, data: null };
        }

        const inputWithTransformedNullValues = evolve({
          jobSize: (jobSize) => {
            if (jobSize === "" || String(jobSize).trim() === "") {
              return 0;
            }
            return typeof jobSize === "number" ? jobSize : parseInt(String(jobSize), 10) || 0;
          },
          material: (material) => ({
            ...material,
            // Prevent stale invalid markup overrides from blocking unrelated proposal saves.
            markup: sanitizeMaterialMarkup(material.markup),
            dailyUnitsComplete: isNaN(material.dailyUnitsComplete)
              ? null
              : material.dailyUnitsComplete?.toString(),
            materialNeeded: material.materialNeeded
              ? typeof material.materialNeeded === "string"
                ? parseFloat(material.materialNeeded) || null
                : material.materialNeeded
              : null,
          }),
          transferTrucks: (transferTrucks) =>
            transferTrucks.map((transferTruck) => ({
              ...transferTruck,
              quantity: !!transferTruck.quantity ? transferTruck.quantity : 0,
            })),
          equipmentPieces: (equipmentPieces) =>
            equipmentPieces.map((equipment) => {
              const existingEquipmentPiece = product.equipmentPieces?.find(
                (equipmentPiece) => equipmentPiece.id === equipment.id
              );
              const isHalfDayEquipmentChargeType =
                existingEquipmentPiece?.chargeType === EquipmentPieceChargeType.HALF_DAY;
              const isFullDayEquipmentChargeType =
                existingEquipmentPiece?.chargeType === EquipmentPieceChargeType.FULL_DAY;
              const inputJobHours = equipment.timeOnJob;
              const usesDaysForEquipment =
                isFullDayEquipmentChargeType || isHalfDayEquipmentChargeType;
              const timeOnJobSeconds = usesDaysForEquipment
                ? convertInputDaysToSeconds(inputJobHours)
                : convertInputHoursToSeconds(inputJobHours);
              return {
                ...equipment,
                timeOnJob: timeOnJobSeconds,
                timeTravel: convertInputHoursToSeconds(equipment.timeTravel),
              };
            }),
          crews: (crews) =>
            crews.map((crew) => {
              const isHalfDayLabourCostType =
                product.material.labourCostType === ProductLabourCostType.HALF_DAY;
              const isFullDayLabourCostType =
                product.material.labourCostType === ProductLabourCostType.FULL_DAY;
              const inputJobHours = crew.timeOnjob;
              const usesDaysForLabour = isFullDayLabourCostType || isHalfDayLabourCostType;
              const timeOnJobSeconds = usesDaysForLabour
                ? convertInputDaysToSeconds(inputJobHours)
                : convertInputHoursToSeconds(inputJobHours);
              return {
                ...crew,
                quantity: crew.quantity ? crew.quantity : null,
                timeOnjob: timeOnJobSeconds,
                timeTravel: convertInputHoursToSeconds(crew.timeTravel),
              };
            }),
          profitPercentageOverride: (v) => (typeof v === "number" && !isNaN(v) ? v : null), // this is needed so as not to send NaN values to the backend
          overallTotalOverride: (overallTotalOverride) =>
            !!overallTotalOverride ? overallTotalOverride : null,
          // Old draft uses CurrencyFormField, which already stores values in cents.
          subcontractorCost: numberOrNull,
          plantProducts: (plantProducts) =>
            plantProducts && plantProducts.length > 0
              ? plantProducts.map((plantProduct) => ({
                  ...plantProduct,
                  materialNeededOverride:
                    typeof plantProduct.materialNeededOverride === "number" &&
                    !isNaN(plantProduct.materialNeededOverride)
                      ? plantProduct.materialNeededOverride
                      : null,
                  rateOverride:
                    typeof plantProduct.rateOverride === "number" &&
                    !isNaN(plantProduct.rateOverride)
                      ? Math.round(plantProduct.rateOverride * 100)
                      : null,
                  applicationRateOverride:
                    typeof plantProduct.applicationRateOverride === "number" &&
                    !isNaN(plantProduct.applicationRateOverride)
                      ? plantProduct.applicationRateOverride
                      : null,
                  areaCoverage: plantProduct.areaCoverage || null,
                  rateOnWeight: plantProduct.rateOnWeight ?? null,
                  transferTruckRoundTripTimeHrsOverride:
                    plantProduct.transferTruckRoundTripTimeHrsOverride || null,
                  transferTruckLoadTimeBuffer: plantProduct.transferTruckLoadTimeBuffer
                    ? +(plantProduct.transferTruckLoadTimeBuffer / 60)
                    : null,
                  transferTruckUnloadTimeBuffer: plantProduct.transferTruckUnloadTimeBuffer
                    ? +(plantProduct.transferTruckUnloadTimeBuffer / 60)
                    : null,
                }))
              : undefined,
          materialNoPlants: cleanMaterialNoPlants,
        })(input);
        // assign last variable
        const parsedInput = inputWithTransformedNullValues as UpdateProposalProductInput;

        const result = await updateProposalProduct({
          variables: { input: parsedInput },
        });
        const success = isSuccess(result.data);
        const data = getData(result);
        if (success) {
          setProductFormStatus(product.id, "fulfilled");
          // ensure pending-change banner clears after a successful auto-save
          setProductDirtyStatus(product.id, false);
          methods.reset(getValues());
          // writeProposal(result.data.updateProposalProduct as Proposal);
        } else {
          setProductFormStatus(product.id, "rejected");
          // keep banner up to prompt manual save
          setProductDirtyStatus(product.id, true);
          // alert that it failed to update
          show({
            message: getGraphQLErrorMessage(
              result,
              getLocalString("proposals.update-proposal-product-failure")
            ),
            options: { variant: "error" },
          });
        }
        return { success, data };
      } finally {
        saveInFlightRef.current = false;
        if (pendingSaveRef.current && isMountedRef.current) {
          pendingSaveRef.current = false;
          window.setTimeout(() => {
            if (!isMountedRef.current) {
              return;
            }
            methods.handleSubmit(onSubmit)();
          }, 0);
        }
      }
    },
    [
      updateProposalProduct,
      setProductFormStatus,
      setProductDirtyStatus,
      methods,
      getValues,
      isSuccess,
      getData,
      product,
      convertInputDaysToSeconds,
      convertInputHoursToSeconds,
      show,
    ]
  );

  const requestSave = useCallback(() => {
    if (saveInFlightRef.current) {
      pendingSaveRef.current = true;
      return;
    }
    methods.handleSubmit(onSubmit)();
  }, [methods, onSubmit]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (isDirty && expandedProduct !== product.id) {
      requestSave();
    }
  }, [isDirty, expandedProduct, product.id, requestSave]);
  useEffect(() => {
    if (product.mapImageDirty) {
      mapImageInterval.current = setInterval(() => {
        requestSave();
      }, 60 * 1000);
      // @ts-ignore
      return () => mapImageInterval.current && clearInterval(mapImageInterval.current);
    }
  }, [product.mapImageDirty, requestSave]);

  useEffect(() => {
    // Let's update the submitFns for this product id
    setProductSubmitFunction(product.id, async () => {
      requestSave();
      return true;
    });
  }, [product.id, requestSave, setProductSubmitFunction]);

  useEffect(() => {
    // Avoid wiping in-progress edits when accordion re-renders; only reset when clean
    if (!methods.formState.isDirty) {
      methods.reset(defaultValues);
    }
  }, [defaultValues, methods, methods.formState.isDirty]);

  // Reset mapDataProduct when product.mapData changes (e.g., when cleared due to address change)
  // useEffect(() => {
  //   setMapDataProduct(product.mapData ? product.mapData : mapData);
  // }, [product.mapData, mapData]);

  const transferTrucksById = transferTrucks.reduce((acc, val) => {
    return {
      ...acc,
      [val.id]: val,
    };
  }, {});

  const saveMapValue = (data: MapOutput) => {
    if (product?.material?.measurementJob?.trim() === ProductMeasurementJob.UNIT) {
      return data?.measurements?.area?.feet ?? data?.measurements?.area?.yards ?? 0;
    }
    const [measurement, unit] =
      measurementMapTranslations[product.material.measurementJob?.trim()]?.split(".");
    return data.measurements[measurement]?.[unit] ?? 0;
  };
  const onEnterPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const keyCode = e.which || e.keyCode;
    if (
      (e.key.toLowerCase() === "enter" || keyCode?.toString() === "13") &&
      e.target["role"] !== "textbox"
    ) {
      setProductDirtyStatus(product.id, false);
      requestSave();
    }
  };
  return (
    <StageProductContext.Provider
      //@ts-ignore
      value={{ product, stage, defaultValues, onSubmit, transferTrucksById }}
    >
      <FormProvider {...methods}>
        <DirtyFieldsTracker id={product.id} />
        <MDBox
          component="form"
          role="form"
          onSubmit={
            //@ts-ignore
            methods.handleSubmit(onSubmit)
          }
          onKeyDown={onEnterPress}
        >
          <MDBox>
            <Grid
              container
              my={1}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item xs={12} md={3}>
                <MDBox display="flex" alignItems="center">
                  <FormField
                    label="Enter Job Size"
                    disabled={inViewMode}
                    shrink
                    {...register("jobSize", {
                      setValueAs: (v) => (v ? parseInt(v?.toString().replace(",", ""), 10) : v), // remove commas in input
                    })}
                  />
                  <MDTypography px={1} variant="body2">
                    {getJobSizeUnitLabel(product)}
                  </MDTypography>
                </MDBox>
              </Grid>
              {!inViewMode && (
                <Grid
                  item
                  xs={12}
                  md={3}
                  display="flex"
                  justifyContent={{ xs: "center", md: "flex-end" }}
                >
                  <Controller
                    name="mapData"
                    control={methods.control}
                    render={({ field }) => (
                      <>
                        <MDButton variant="gradient" color="secondary" onClick={onOpen}>
                          Measure on Map
                        </MDButton>
                        {mapDataProduct && (
                          <MapModal
                            initialArea={mapDataProduct ? JSON.parse(mapDataProduct) : null}
                            initialCenter={
                              stage?.address?.latitude && stage?.address?.longitude
                                ? [Number(stage.address.latitude), Number(stage.address.longitude)]
                                : null
                            }
                            open={open}
                            hideLocation
                            onClose={onClose}
                            showTools={[
                              product.material.measurementJob?.trim() as keyof typeof ProductMeasurementJob,
                            ]}
                            onSave={async (data) => {
                              setProductDirtyStatus(product.id, false);
                              setMapDataProduct(JSON.stringify(data.geojson));
                              field.onChange(JSON.stringify(data.geojson));
                              methods.setValue("jobSize", saveMapValue(data));
                              methods.setValue("mapData", JSON.stringify(data.geojson));
                              //@ts-ignore
                              let saveResult: { success: boolean } | undefined;
                              await methods.handleSubmit(async (input) => {
                                saveResult = await onSubmit(input);
                              })();
                              return saveResult?.success ?? false;
                            }}
                          />
                        )}
                      </>
                    )}
                  />
                </Grid>
              )}
            </Grid>
            <Accordion defaultExpanded elevation={3}>
              <AccordionSummary
                sx={{ backgroundColor: "rgba(100, 100, 150, 0.1)" }}
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <MDTypography variant="h6">Proposal Defaults and Inputs</MDTypography>
              </AccordionSummary>
              <AccordionDetails>
                <ProposalDefaultsAndInputs />
              </AccordionDetails>
            </Accordion>
            {!isSubcontracted && !isUnitPriced && (
              <>
                <Accordion defaultExpanded elevation={3}>
                  <AccordionSummary
                    sx={{ backgroundColor: "rgba(100, 100, 150, 0.1)" }}
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                  >
                    <div
                      style={{
                        justifyContent: "space-between",
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <MDTypography variant="h5">Material Costs</MDTypography>
                      {new Intl.NumberFormat(undefined, {
                        currency: "USD",
                        style: "currency",
                      }).format((+product.costMaterial + +product.costTransferTruck) / 100)}
                    </div>
                  </AccordionSummary>
                  <AccordionDetails>
                    <MaterialCosts
                      onAddMaterialModalOpen={onAddMaterialModalOpen}
                      onAddMaterialNoPlantModalOpen={onAddMaterialNoPlantModalOpen}
                    />
                  </AccordionDetails>
                </Accordion>
                <Accordion defaultExpanded elevation={3}>
                  <AccordionSummary
                    sx={{ backgroundColor: "rgba(150, 150, 175, 0.1)" }}
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                  >
                    <div
                      style={{
                        justifyContent: "space-between",
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <MDTypography variant="h5">Equipment Costs</MDTypography>
                      <div
                        style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        <span style={{ display: "flex", alignItems: "center" }}>
                          {formatCentsToUSD(product.costEquipment)}
                        </span>
                      </div>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails>
                    <EquipmentCostsSubForm averageWorkingHoursPerDay={averageWorkingHoursPerDay} />
                  </AccordionDetails>
                </Accordion>
                <Accordion defaultExpanded elevation={3}>
                  <AccordionSummary
                    sx={{ backgroundColor: "rgba(100, 100, 150, 0.1)" }}
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                  >
                    <div
                      style={{
                        justifyContent: "space-between",
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <MDTypography variant="h5">Labor Costs</MDTypography>
                      <div
                        style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        <span style={{ display: "flex", alignItems: "center" }}>
                          {formatCentsToUSD(product.costLabour)}
                        </span>
                      </div>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails>
                    <LabourCostsSubForm averageWorkingHoursPerDay={averageWorkingHoursPerDay} />
                  </AccordionDetails>
                </Accordion>
              </>
            )}
            {!isSubcontracted && (
              <Accordion defaultExpanded elevation={3}>
                <AccordionSummary
                  sx={{ backgroundColor: "rgba(100, 100, 150, 0.1)" }}
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <div
                    style={{
                      justifyContent: "space-between",
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <MDTypography variant="h5">Additional Costs</MDTypography>
                    <div
                      style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                    >
                      <span style={{ display: "flex", alignItems: "center" }}>
                        {formatCentsToUSD(product.costAdditional)}
                      </span>
                    </div>
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <AdditionalCostsSubForm />
                </AccordionDetails>
              </Accordion>
            )}
            <MDBox py={2}>
              <MDBox display="flex" justifyContent="space-between" py={2}>
                <MDTypography mb={2} variant="h6">
                  Product Files
                </MDTypography>
                <AddProjectFileToProposal id={product.id} />
              </MDBox>
              <MDBox>
                <ProposalProductFiles />
              </MDBox>
              <MDBox
                sx={{
                  width: "100%",
                  position: "relative",
                  "& .uppy-DragDrop-inner": { padding: "16px 0" },
                  "& .uppy-DragDrop-label": { fontSize: "1em" },
                }}
              >
                <AddFilesToProduct />
              </MDBox>
            </MDBox>
            <Grid item xs={12} my={0}>
              <MDTypography variant="h6" my={0}>
                Product Summary
              </MDTypography>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={measurementJobUnit && !isUnitPriced ? 4 : 6} my={1}></Grid>

                {measurementJobUnit && !isUnitPriced && (
                  <Grid item xs={12} md={4}>
                    <CurrencyFormField
                      label="Unit Price"
                      placeholder={+product.material.costMaterial / 100}
                      disabled={inViewMode || !!getValues("material.unitPrice") || isSubcontracted}
                      name="material.unitPrice"
                      control={control}
                      {...currencyEndAdornment}
                    />
                  </Grid>
                )}

                <Grid item xs={12} md={measurementJobUnit && !isUnitPriced ? 4 : 6}></Grid>
              </Grid>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead sx={{ display: "table-header-group" }}>
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        align="center"
                        sx={{ backgroundColor: "rgba(150, 150, 200, 0.3)" }}
                      >
                        Product Summary
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                      <TableCell component="th" scope="row">
                        Total Costs
                      </TableCell>
                      <TableCell align="right">Profit</TableCell>
                      <TableCell align="right">Profit Margin %</TableCell>
                      <TableCell align="right">Discount</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                    <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                      <TableCell component="th" scope="row">
                        {formatCentsToUSD(+product.costTotal)}
                      </TableCell>
                      <TableCell align="right">{formatCentsToUSD(product.profit)}</TableCell>
                      <TableCell align="right">
                        <FormField
                          align="right"
                          placeholder={product.profitPercentage}
                          disabled={inViewMode || isSubcontracted}
                          type="number"
                          className="no-spinners"
                          inputProps={{
                            min: 1,
                            max: 99,
                            step: 0.01,
                            onKeyDown: (e: React.KeyboardEvent) => {
                              if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                                e.preventDefault();
                              }
                            },
                          }}
                          {...register("profitPercentageOverride", {
                            valueAsNumber: true,
                            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                              if (e.target.value) {
                                setValue("overallTotalOverride", "", { shouldDirty: true });
                              }
                            },
                          })}
                        />
                      </TableCell>
                      <TableCell align="right">{formatCentsToUSD(product.discount)}</TableCell>
                      <TableCell align="right">
                        <CurrencyFormField
                          placeholder={+product.overallTotal / 100}
                          disabled={inViewMode}
                          name="overallTotalOverride"
                          control={control}
                          className="no-spinners"
                          inputProps={{
                            onKeyDown: (e: React.KeyboardEvent) => {
                              if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                                e.preventDefault();
                              }
                            },
                            onInput: (e: React.FormEvent<HTMLInputElement>) => {
                              if ((e.target as HTMLInputElement).value) {
                                setValue("profitPercentageOverride", "" as any, {
                                  shouldDirty: true,
                                });
                              }
                            },
                          }}
                          {...currencyEndAdornment}
                          key={isSubmitSuccessful}
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </MDBox>
          {!inViewMode && (
            <MDBox
              textAlign="right"
              sx={{ display: "flex", justifyContent: "flex-end", gap: 1, paddingTop: "10px" }}
            >
              <MDButton type="submit" variant="gradient" color="success">
                Save Stage Product
              </MDButton>
              <DeleteStageProduct
                acceptFn={() => (handleDelete ? handleDelete(product.id) : () => {})}
              />
            </MDBox>
          )}
        </MDBox>
      </FormProvider>
      {!isSubcontracted && !isUnitPriced && (
        <AddMaterialModal
          open={addMaterialModalOpen}
          onClose={onAddMaterialModalClose}
          product={product}
        />
      )}
      {!isSubcontracted && !isUnitPriced && (
        <AddMaterialNoPlantModal
          open={addMaterialNoPlantModalOpen}
          onClose={onAddMaterialNoPlantModalClose}
          product={product}
        />
      )}
    </StageProductContext.Provider>
  );
});

export default StageProduct;
